import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LayoutDashboard, UtensilsCrossed, CreditCard, QrCode,
  Lock, CheckCircle, RefreshCw, X,
  Image, Save, Search, Plus, Pencil,
  FolderOpen, Tag, Bell, BookOpen,
  CalendarDays, Users, Mail,
  FileDown, Radio, Banknote, Percent, ClipboardList, DollarSign, Zap, Lightbulb, Star, Eye, Sparkles,
  LogOut, ArrowLeftRight, Bike, ExternalLink, ChefHat, Monitor, Receipt, Heart, BarChart3, Printer as PrinterIcon
} from 'lucide-react';
import axios from 'axios';
import { HappyHourTab } from './admin/HappyHourTab';
import { ReservationsTab } from './admin/ReservationsTab';
import { DailyReportTab } from './admin/DailyReportTab';
import { PromoCodesTab } from './admin/PromoCodesTab';
import { ServersTab } from './admin/ServersTab';
import { OrdersTab } from './admin/OrdersTab';
import { MenuTab } from './admin/MenuTab';
import { CategoriesTab } from './admin/CategoriesTab';
import { EventsTab } from './admin/EventsTab';
import { ClientsTab } from './admin/ClientsTab';
import { PaymentsTab } from './admin/PaymentsTab';
import { TablesTab } from './admin/TablesTab';
import { AccountingTab } from './admin/AccountingTab';
import { SuggestionsTab } from './admin/SuggestionsTab';
import { ReviewsTab } from './admin/ReviewsTab';
import { ClientPreviewTab } from './admin/ClientPreviewTab';
import { AdminPosTab } from './admin/AdminPosTab';
import { OnlineSalesTab } from './admin/OnlineSalesTab';
import { OrderHistoryTab } from './admin/OrderHistoryTab';
import { ZReportTab } from './admin/ZReportTab';
import { PrinterTab } from './admin/PrinterTab';
import { LoyaltyTab } from './admin/LoyaltyTab';
import { AnalyticsTab } from './admin/AnalyticsTab';
import AssistantChat from '../components/AssistantChat';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminRole, setAdminRole] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  const [menuCategory, setMenuCategory] = useState('all');
  const [isTranslating, setIsTranslating] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showNewItem, setShowNewItem] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', description: '', price: '', cost_price: '', category_id: '', image_url: '', is_popular: false, tags: [], stock_count: null, spice_level: 0 });
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCat, setNewCat] = useState({ name: '', name_fr: '', section: 'cuisine' });
  const [editingCat, setEditingCat] = useState(null);
  const [adminNotif, setAdminNotif] = useState(null);
  const eventSourceRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', title_fr: '', description: '', description_fr: '', event_type: 'event', date: '', time_range: '', image_url: '', send_email: false });
  const [analytics, setAnalytics] = useState(null);
  const [happyHours, setHappyHours] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [liveIndicator, setLiveIndicator] = useState(false);
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [cashDialogOrder, setCashDialogOrder] = useState(null);
  const [cashAmount, setCashAmount] = useState('');
  const [cashTip, setCashTip] = useState('');
  const [discountDialogOrder, setDiscountDialogOrder] = useState(null);
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');

  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications('admin');

  useEffect(() => {
    const authStatus = sessionStorage.getItem('adminAuth');
    const savedRole = sessionStorage.getItem('adminRole');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      if (savedRole) setAdminRole(savedRole);
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      connectSSE();
      return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
    }
  }, [isAuthenticated]);

  const connectSSE = () => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    const es = new EventSource(`${API}/events/admin`);
    eventSourceRef.current = es;
    es.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_order') {
          fetchData();
          setAdminNotif(`Nouvelle commande - Table ${data.table_id}`);
          try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const osc = ctx.createOscillator(); const gain = ctx.createGain(); osc.connect(gain); gain.connect(ctx.destination); osc.frequency.value = 880; gain.gain.value = 0.15; osc.start(); osc.stop(ctx.currentTime + 0.15); setTimeout(() => { const o2 = ctx.createOscillator(); const g2 = ctx.createGain(); o2.connect(g2); g2.connect(ctx.destination); o2.frequency.value = 1100; g2.gain.value = 0.15; o2.start(); o2.stop(ctx.currentTime + 0.15); }, 200); } catch(e) {}
          setTimeout(() => setAdminNotif(null), 5000);
        } else if (data.type === 'waiter_call') {
          fetchData();
          setAdminNotif(`Table ${data.table_id} appelle un serveur !`);
          try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const osc = ctx.createOscillator(); const gain = ctx.createGain(); osc.connect(gain); gain.connect(ctx.destination); osc.frequency.value = 1200; gain.gain.value = 0.2; osc.start(); osc.stop(ctx.currentTime + 0.1); setTimeout(() => { const o2 = ctx.createOscillator(); const g2 = ctx.createGain(); o2.connect(g2); g2.connect(ctx.destination); o2.frequency.value = 1200; g2.gain.value = 0.2; o2.start(); o2.stop(ctx.currentTime + 0.1); }, 150); } catch(e) {}
          try { const res = await axios.get(`${API}/admin/waiter-calls`); setWaiterCalls(res.data); } catch(e) {}
          setTimeout(() => setAdminNotif(null), 8000);
        } else if (data.type === 'status_update') { fetchData(); }
        setLiveIndicator(true);
        setTimeout(() => setLiveIndicator(false), 2000);
      } catch(e) {}
    };
    es.onerror = () => { es.close(); setTimeout(connectSSE, 3000); };
  };

  const handleLogin = async () => {
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setAdminRole(res.data.role || 'admin');
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      sessionStorage.setItem('adminRole', res.data.role || 'admin');
      fetchData();
    } catch (err) { setError('Mot de passe incorrect'); }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/orders`), axios.get(`${API}/admin/payments`), axios.get(`${API}/admin/tables`),
        axios.get(`${API}/admin/menu`), axios.get(`${API}/admin/categories`), axios.get(`${API}/admin/events`),
        axios.get(`${API}/admin/customers`), axios.get(`${API}/admin/analytics`), axios.get(`${API}/admin/happy-hours`),
        axios.get(`${API}/admin/reservations`), axios.get(`${API}/admin/birthdays/upcoming`)
      ]);
      const get = (i) => results[i]?.status === 'fulfilled' ? results[i].value.data : [];
      setOrders(get(0)); setPayments(get(1)); setTables(get(2));
      setMenuItems(get(3)); setCategories(get(4)); setEvents(get(5));
      setCustomers(get(6)); setAnalytics(results[7]?.status === 'fulfilled' ? results[7].value.data : {}); setHappyHours(get(8));
      setReservations(get(9)); setBirthdays(get(10));
    } catch (error) { console.error('Error fetching data:', error); }
    finally { setIsLoading(false); }
  }, []);

  // ─── Actions ───
  const updateOrderStatus = async (orderId, status) => {
    try { await axios.patch(`${API}/orders/${orderId}/status?status=${status}`); fetchData(); } catch (e) { console.error(e); }
  };
  const closeOrder = async (orderId) => {
    try { await axios.patch(`${API}/admin/orders/${orderId}/close`); fetchData(); } catch (e) { console.error(e); }
  };
  const dismissWaiterCall = async (callId) => {
    try { await axios.patch(`${API}/admin/waiter-calls/${callId}/dismiss`); setWaiterCalls(prev => prev.filter(c => c.id !== callId)); } catch (e) { console.error(e); }
  };
  const recordCashPayment = async () => {
    if (!cashDialogOrder || !cashAmount) return;
    try { await axios.post(`${API}/admin/orders/${cashDialogOrder.id}/cash-payment`, { amount: parseFloat(cashAmount), tip_amount: parseFloat(cashTip) || 0 }); setCashDialogOrder(null); setCashAmount(''); setCashTip(''); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const recordOtherPayment = async (order, method) => {
    if (!window.confirm(`Confirmer le paiement par ${method === 'cb' ? 'Carte Bancaire' : method === 'promptpay' ? 'PromptPay' : 'Comptoir'} ?`)) return;
    try { await axios.post(`${API}/admin/orders/${order.id}/payment`, { payment_method: method }); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const applyDiscount = async () => {
    if (!discountDialogOrder || !discountValue) return;
    try { await axios.post(`${API}/admin/orders/${discountDialogOrder.id}/discount`, { discount_type: discountType, discount_value: parseFloat(discountValue) }); setDiscountDialogOrder(null); setDiscountValue(''); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const generateQR = async (tableNumber) => {
    try { const baseUrl = window.location.origin; const response = await axios.get(`${API}/admin/qr/${tableNumber}?base_url=${encodeURIComponent(baseUrl)}`); setQrData(response.data); } catch (e) { console.error(e); }
  };
  const saveMenuItem = async (itemId, data) => {
    try { await axios.patch(`${API}/admin/menu/${itemId}`, data); setEditDialogOpen(false); setEditingItem(null); setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2500); fetchData(); } catch (e) { alert('Erreur lors de la sauvegarde'); }
  };
  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try { await axios.delete(`${API}/admin/menu/${itemId}`); fetchData(); setEditingItem(null); } catch (e) { console.error(e); }
  };
  const toggleStock = async (itemId) => {
    try { await axios.patch(`${API}/admin/menu/${itemId}/toggle-stock`); fetchData(); } catch (e) { console.error(e); }
  };
  const createMenuItem = async () => {
    if (!newItem.name || !newItem.price || !newItem.category_id) return;
    try { await axios.post(`${API}/admin/menu`, { ...newItem, price: parseFloat(newItem.price), cost_price: parseFloat(newItem.cost_price) || 0, stock_count: newItem.stock_count, spice_level: newItem.spice_level || 0 }); setNewItem({ name: '', description: '', price: '', cost_price: '', category_id: '', image_url: '', is_popular: false, tags: [], stock_count: null, spice_level: 0 }); setShowNewItem(false); fetchData(); } catch (e) { console.error(e); }
  };
  const translateDescription = async (itemId, frenchText) => {
    if (!frenchText.trim()) return;
    setIsTranslating(true);
    try { const response = await axios.post(`${API}/admin/menu/${itemId}/translate`, { text: frenchText, source_lang: "fr" }); if (response.data.translations) { setEditingItem(prev => ({ ...prev, description_translations: response.data.translations, translationSuccess: true })); } fetchData(); } catch (e) { console.error(e); } finally { setIsTranslating(false); }
  };
  const createCategory = async () => {
    if (!newCat.name) return;
    try { await axios.post(`${API}/admin/categories`, newCat); setNewCat({ name: '', name_fr: '', section: 'cuisine' }); setShowNewCat(false); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const updateCategory = async (catId, data) => {
    try { await axios.patch(`${API}/admin/categories/${catId}`, data); setEditingCat(null); fetchData(); } catch (e) { console.error(e); }
  };
  const deleteCategory = async (catId) => {
    if (!window.confirm('Supprimer cette categorie ?')) return;
    try { await axios.delete(`${API}/admin/categories/${catId}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Erreur lors de la suppression'); }
  };
  const moveCategoryOrder = async (catId, direction) => {
    if (direction === 'refresh') { fetchData(); return; }
    const sorted = [...categories].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex(c => c.id === catId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const newOrder = sorted.map((c) => ({ ...c }));
    const tempOrder = newOrder[idx].display_order;
    newOrder[idx].display_order = newOrder[swapIdx].display_order;
    newOrder[swapIdx].display_order = tempOrder;
    try { await axios.post(`${API}/admin/categories/reorder`, newOrder.map(c => ({ id: c.id, display_order: c.display_order }))); fetchData(); } catch (e) { console.error(e); }
  };
  const createEvent = async () => {
    if (!newEvent.title) return;
    try { await axios.post(`${API}/admin/events`, newEvent); setNewEvent({ title: '', title_fr: '', description: '', description_fr: '', event_type: 'event', date: '', time_range: '', image_url: '', send_email: false }); setShowNewEvent(false); fetchData(); } catch (e) { console.error(e); }
  };
  const deleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet evenement ?')) return;
    try { await axios.delete(`${API}/admin/events/${eventId}`); fetchData(); } catch (e) { console.error(e); }
  };
  const sendEventEmails = async (eventId) => {
    if (!window.confirm(`Envoyer l'email a ${customers.length} client(s) ?`)) return;
    try { const res = await axios.post(`${API}/admin/events/${eventId}/send-emails`); alert(`Envoi en cours vers ${res.data.target_count} clients`); } catch (e) { console.error(e); }
  };
  const sendBirthdayEmails = async () => {
    try { const res = await axios.post(`${API}/admin/send-birthday-emails`); alert(`${res.data.sent} email(s) d'anniversaire envoye(s)`); } catch (e) { console.error(e); }
  };

  const filteredMenuItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(menuSearch.toLowerCase());
    const matchesCategory = menuCategory === 'all' || item.category_id === menuCategory || item.category === menuCategory;
    return matchesSearch && matchesCategory;
  });

  const formatPrice = (price) => `฿${price?.toLocaleString() || 0}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleString();

  // ─── Login Screen ───
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4 admin-theme">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-lg">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#0A422D] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D241D]">Administration</h1>
            <p className="text-[#6E5E52] text-sm mt-1">Entrez le mot de passe</p>
          </div>
          {error && <div className="bg-[#E63946]/10 text-[#E63946] px-4 py-2 rounded-xl mb-4 text-sm text-center">{error}</div>}
          <div className="space-y-4">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe" className="h-12 rounded-xl border-[#E5DCD3]"
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()} data-testid="admin-password-input" />
            <Button onClick={handleLogin} className="w-full h-12 btn-primary rounded-full font-semibold" data-testid="admin-login-btn">
              Connexion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] admin-theme">
      {adminNotif && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#E16833] text-white px-4 py-3 flex items-center justify-center gap-2 shadow-lg animate-slide-down" data-testid="admin-notification">
          <Bell className="w-5 h-5" /><span className="font-semibold">{adminNotif}</span>
        </div>
      )}
      <header className="bg-[#0A422D] text-white px-4 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-6 h-6" />
            <h1 className="text-xl font-bold">HIBI<span className="text-[#D4A853]">SAAS</span> Admin</h1>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${adminRole === 'admin' ? 'bg-[#D4A853] text-[#0D0D0D]' : 'bg-white/20 text-white'}`}>
              {adminRole === 'admin' ? 'ADMIN' : 'MANAGER'}
            </span>
            <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-colors ${liveIndicator ? 'bg-red-500 text-white' : 'bg-white/10 text-white/60'}`}>
              <Radio className={`w-3 h-3 ${liveIndicator ? 'animate-pulse' : ''}`} /><span>LIVE</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {pushSupported && !pushSubscribed && (
              <Button onClick={pushSubscribe} variant="ghost" className="text-white hover:bg-white/10" data-testid="push-subscribe-btn">
                <Bell className="w-5 h-5" />
              </Button>
            )}
            {pushSubscribed && (
              <span className="text-xs text-white/50 flex items-center gap-1 mr-1"><Bell className="w-3.5 h-3.5 text-[#D4A853]" /></span>
            )}
            {adminRole === 'admin' && (
              <div className="relative group">
                <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="export-btn"><FileDown className="w-5 h-5" /></Button>
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-[#E5DCD3] w-44 hidden group-hover:block z-50">
                  <a href={`${API}/admin/export/orders`} className="block px-4 py-2.5 text-sm text-[#2D241D] hover:bg-[#F3EDE5] rounded-t-xl" data-testid="export-orders">Commandes (CSV)</a>
                  <a href={`${API}/admin/export/customers`} className="block px-4 py-2.5 text-sm text-[#2D241D] hover:bg-[#F3EDE5]" data-testid="export-customers">Clients (CSV)</a>
                  <a href={`${API}/admin/export/revenue`} className="block px-4 py-2.5 text-sm text-[#2D241D] hover:bg-[#F3EDE5] rounded-b-xl" data-testid="export-revenue">Revenus (CSV)</a>
                </div>
              </div>
            )}
            <Button onClick={fetchData} variant="ghost" className="text-white hover:bg-white/10" disabled={isLoading}>
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            {adminRole === 'admin' && (
              <Button onClick={() => {
                setAdminRole('manager');
                sessionStorage.setItem('adminRole', 'manager');
              }} variant="ghost" className="text-white hover:bg-white/10 text-xs gap-1" data-testid="switch-to-manager-btn">
                <ArrowLeftRight className="w-4 h-4" /> Manager
              </Button>
            )}
            {adminRole === 'manager' && (
              <Button onClick={() => {
                setPassword('');
                setIsAuthenticated(false);
                sessionStorage.removeItem('adminAuth');
                sessionStorage.removeItem('adminRole');
              }} variant="ghost" className="text-white hover:bg-white/10 text-xs gap-1" data-testid="switch-to-admin-btn">
                <ArrowLeftRight className="w-4 h-4" /> Admin
              </Button>
            )}
            <Button onClick={() => {
              setIsAuthenticated(false);
              setPassword('');
              sessionStorage.removeItem('adminAuth');
              sessionStorage.removeItem('adminRole');
            }} variant="ghost" className="text-white/60 hover:bg-white/10 hover:text-red-400" data-testid="admin-logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Quick access bar */}
      <div className="bg-[#0A422D]/90 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-1.5 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <span className="text-[10px] text-white/40 font-bold flex-shrink-0 mr-1">ACCES:</span>
          <Link to="/caisse" target="_blank" className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#E16833] text-white text-[10px] font-bold hover:bg-[#C95A2A] transition-colors flex-shrink-0" data-testid="quick-caisse">
            <Monitor className="w-3 h-3" /> Caisse
          </Link>
          <Link to="/server" target="_blank" className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 text-white text-[10px] font-bold hover:bg-white/20 transition-colors flex-shrink-0" data-testid="quick-server">
            <UtensilsCrossed className="w-3 h-3" /> Serveur
          </Link>
          <Link to="/kitchen" target="_blank" className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 text-white text-[10px] font-bold hover:bg-white/20 transition-colors flex-shrink-0" data-testid="quick-kitchen">
            <ChefHat className="w-3 h-3" /> Cuisine
          </Link>
          <Link to="/bar" target="_blank" className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 text-white text-[10px] font-bold hover:bg-white/20 transition-colors flex-shrink-0" data-testid="quick-bar">
            <Zap className="w-3 h-3" /> Bar
          </Link>
          <Link to="/menu" target="_blank" className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#D4A853] text-black text-[10px] font-bold hover:bg-[#C49A48] transition-colors flex-shrink-0" data-testid="quick-menu">
            <ExternalLink className="w-3 h-3" /> Menu Client
          </Link>
        </div>
      </div>

      {waiterCalls.length > 0 && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 max-w-6xl mx-auto" data-testid="waiter-calls-banner">
          {waiterCalls.map(call => (
            <div key={call.id} className="flex items-center justify-between py-1">
              <span className="text-sm text-red-700 flex items-center gap-2">
                <Bell className="w-4 h-4 animate-bounce" />
                <strong>Table {call.table_id}</strong> - {call.message}
                <span className="text-xs text-red-400">{new Date(call.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
              </span>
              <Button size="sm" onClick={() => dismissWaiterCall(call.id)}
                className="bg-red-500 hover:bg-red-600 text-white rounded-lg h-7 text-xs px-3" data-testid={`dismiss-call-admin-${call.id}`}>OK</Button>
            </div>
          ))}
        </div>
      )}

      {saveSuccess && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-50 bg-[#2D6A4F] text-white px-5 py-3 rounded-2xl shadow-lg flex items-center gap-2 animate-slide-up" data-testid="save-success-toast">
          <CheckCircle className="w-5 h-5" /><span className="font-medium text-sm">Sauvegarde reussie !</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-4">
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="w-full bg-white rounded-2xl p-2 mb-4 grid grid-cols-7 auto-rows-auto gap-1 !h-auto flex-wrap">
            <TabsTrigger value="orders" className="rounded-xl data-[state=active]:bg-[#0A422D] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1 h-auto" data-testid="tab-orders"><UtensilsCrossed className="w-4 h-4" /><span>Commandes</span></TabsTrigger>
            <TabsTrigger value="pos" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1 h-auto" data-testid="tab-pos"><CreditCard className="w-4 h-4" /><span>Caisse</span></TabsTrigger>
            <TabsTrigger value="categories" className="rounded-xl data-[state=active]:bg-[#0A422D] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-categories"><FolderOpen className="w-4 h-4" /><span>Categories</span></TabsTrigger>
            <TabsTrigger value="menu" className="rounded-xl data-[state=active]:bg-[#0A422D] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-menu"><Image className="w-4 h-4" /><span>Menu</span></TabsTrigger>
            <TabsTrigger value="tables" className="rounded-xl data-[state=active]:bg-[#0A422D] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-tables"><QrCode className="w-4 h-4" /><span>Tables</span></TabsTrigger>
            <TabsTrigger value="servers" className="rounded-xl data-[state=active]:bg-[#0A422D] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-servers"><Users className="w-4 h-4" /><span>Serveurs</span></TabsTrigger>
            <TabsTrigger value="clients" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-clients"><Users className="w-4 h-4" /><span>Clients</span></TabsTrigger>
            <TabsTrigger value="accounting" className="rounded-xl data-[state=active]:bg-[#D4A853] data-[state=active]:text-black text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-accounting"><BookOpen className="w-4 h-4" /><span>Compta</span></TabsTrigger>
            <TabsTrigger value="promos" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-promos"><Tag className="w-4 h-4" /><span>Promos</span></TabsTrigger>
            <TabsTrigger value="events" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-events"><CalendarDays className="w-4 h-4" /><span>Events</span></TabsTrigger>
            <TabsTrigger value="happyhour" className="rounded-xl data-[state=active]:bg-[#D4A853] data-[state=active]:text-black text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-happyhour"><Zap className="w-4 h-4" /><span>Happy H.</span></TabsTrigger>
            <TabsTrigger value="reservations" className="rounded-xl data-[state=active]:bg-[#D4A853] data-[state=active]:text-black text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-reservations"><CalendarDays className="w-4 h-4" /><span>Reserv.</span></TabsTrigger>
            <TabsTrigger value="report" className="rounded-xl data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-report"><ClipboardList className="w-4 h-4" /><span>Rapport</span></TabsTrigger>
            <TabsTrigger value="history" className="rounded-xl data-[state=active]:bg-[#6366F1] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-history"><Search className="w-4 h-4" /><span>Historique</span></TabsTrigger>
            <TabsTrigger value="z-report" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-z-report"><Receipt className="w-4 h-4" /><span>Z Caisse</span></TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-xl data-[state=active]:bg-[#6366F1] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-analytics"><BarChart3 className="w-4 h-4" /><span>Analytics</span></TabsTrigger>
            <TabsTrigger value="loyalty" className="rounded-xl data-[state=active]:bg-red-500 data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-loyalty"><Heart className="w-4 h-4" /><span>Fidelite</span></TabsTrigger>
            <TabsTrigger value="printer" className="rounded-xl data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-printer"><PrinterIcon className="w-4 h-4" /><span>Imprimante</span></TabsTrigger>
            <TabsTrigger value="suggestions" className="rounded-xl data-[state=active]:bg-[#D4A853] data-[state=active]:text-black text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-suggestions"><Lightbulb className="w-4 h-4" /><span>Suggestions</span></TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-xl data-[state=active]:bg-[#D4A853] data-[state=active]:text-black text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-reviews"><Star className="w-4 h-4" /><span>Avis</span></TabsTrigger>
            <TabsTrigger value="online" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-online"><Bike className="w-4 h-4" /><span>En ligne</span></TabsTrigger>
            <TabsTrigger value="preview" className="rounded-xl data-[state=active]:bg-[#E16833] data-[state=active]:text-white text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-preview"><Eye className="w-4 h-4" /><span>Apercu</span></TabsTrigger>
            <TabsTrigger value="assistant" className="rounded-xl data-[state=active]:bg-[#D4A853] data-[state=active]:text-[#0C0C0C] text-[10px] sm:text-xs flex flex-col items-center gap-0.5 py-2 px-1" data-testid="tab-assistant"><Sparkles className="w-4 h-4" /><span>Assistant</span></TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-0">
            <OrdersTab orders={orders} analytics={analytics} reservations={reservations}
              birthdays={birthdays} adminRole={adminRole} updateOrderStatus={updateOrderStatus}
              closeOrder={closeOrder} formatPrice={formatPrice} formatDate={formatDate}
              customers={customers}
              onCashPayment={(order, afterDiscount) => { setCashAmount(String(Math.max(afterDiscount - (order.paid_amount || 0), 0))); setCashTip(''); setCashDialogOrder(order); }}
              onCbPayment={(order) => recordOtherPayment(order, 'cb')}
              onOtherPayment={(order, method) => recordOtherPayment(order, method)}
              onDiscount={(order) => { setDiscountType('percent'); setDiscountValue(''); setDiscountDialogOrder(order); }} />
          </TabsContent>
          <TabsContent value="pos" className="mt-0">
            <AdminPosTab menuItems={menuItems} categories={categories} tables={tables}
              formatPrice={formatPrice} fetchData={fetchData} />
          </TabsContent>
          <TabsContent value="categories" className="mt-0">
            <CategoriesTab categories={categories} showNewCat={showNewCat} setShowNewCat={setShowNewCat}
              newCat={newCat} setNewCat={setNewCat} createCategory={createCategory}
              editingCat={editingCat} setEditingCat={setEditingCat} updateCategory={updateCategory}
              deleteCategory={deleteCategory} moveCategoryOrder={moveCategoryOrder} adminRole={adminRole} />
          </TabsContent>
          <TabsContent value="menu" className="mt-0">
            <MenuTab menuItems={menuItems} categories={categories} adminRole={adminRole}
              menuSearch={menuSearch} setMenuSearch={setMenuSearch} menuCategory={menuCategory}
              setMenuCategory={setMenuCategory} filteredMenuItems={filteredMenuItems}
              showNewItem={showNewItem} setShowNewItem={setShowNewItem} newItem={newItem}
              setNewItem={setNewItem} createMenuItem={createMenuItem} editingItem={editingItem}
              setEditingItem={setEditingItem} editDialogOpen={editDialogOpen}
              setEditDialogOpen={setEditDialogOpen} saveMenuItem={saveMenuItem}
              deleteMenuItem={deleteMenuItem} toggleStock={toggleStock}
              translateDescription={translateDescription} isTranslating={isTranslating}
              formatPrice={formatPrice} onRefresh={fetchData} />
          </TabsContent>
          <TabsContent value="events" className="mt-0">
            <EventsTab events={events} customers={customers} showNewEvent={showNewEvent}
              setShowNewEvent={setShowNewEvent} newEvent={newEvent} setNewEvent={setNewEvent}
              createEvent={createEvent} deleteEvent={deleteEvent} sendEventEmails={sendEventEmails}
              sendBirthdayEmails={sendBirthdayEmails} />
          </TabsContent>
          <TabsContent value="clients" className="mt-0"><ClientsTab customers={customers} onRefresh={fetchData} /></TabsContent>
          <TabsContent value="tables" className="mt-0">
            <TablesTab tables={tables} onRefresh={fetchData} generateQR={generateQR}
              qrData={qrData} adminRole={adminRole} />
          </TabsContent>
          <TabsContent value="happyhour" className="mt-0"><HappyHourTab happyHours={happyHours} categories={categories} onRefresh={fetchData} /></TabsContent>
          <TabsContent value="reservations" className="mt-0"><ReservationsTab reservations={reservations} onRefresh={fetchData} /></TabsContent>
          <TabsContent value="report" className="mt-0"><DailyReportTab adminRole={adminRole} /></TabsContent>
          <TabsContent value="history" className="mt-0"><OrderHistoryTab formatPrice={formatPrice} /></TabsContent>
          <TabsContent value="z-report" className="mt-0"><ZReportTab formatPrice={formatPrice} /></TabsContent>
          <TabsContent value="analytics" className="mt-0"><AnalyticsTab /></TabsContent>
          <TabsContent value="loyalty" className="mt-0"><LoyaltyTab /></TabsContent>
          <TabsContent value="printer" className="mt-0"><PrinterTab /></TabsContent>
          <TabsContent value="promos" className="mt-0"><PromoCodesTab onRefresh={fetchData} /></TabsContent>
          <TabsContent value="servers" className="mt-0"><ServersTab tables={tables} /></TabsContent>
          <TabsContent value="accounting" className="mt-0"><AccountingTab adminRole={adminRole} /></TabsContent>
          <TabsContent value="suggestions" className="mt-0"><SuggestionsTab menuItems={menuItems} categories={categories} /></TabsContent>
          <TabsContent value="reviews" className="mt-0"><ReviewsTab /></TabsContent>
          <TabsContent value="online" className="mt-0"><OnlineSalesTab adminRole={adminRole} /></TabsContent>
          <TabsContent value="preview" className="mt-0"><ClientPreviewTab /></TabsContent>
          <TabsContent value="assistant" className="mt-0">
            <div className="h-[calc(100vh-220px)]">
              <AssistantChat fullscreen={true} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cash Payment Dialog */}
      {cashDialogOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCashDialogOrder(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="cash-dialog">
            <h3 className="font-bold text-lg text-[#2D241D] mb-4 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#2D6A4F]" /> Paiement Cash
            </h3>
            <p className="text-sm text-[#6E5E52] mb-4">Table {cashDialogOrder.table_id} - {formatPrice(cashDialogOrder.total - (cashDialogOrder.discount_amount || 0))}</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Montant recu (THB)</label>
                <Input type="number" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)}
                  className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="cash-amount-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Pourboire (optionnel)</label>
                <Input type="number" value={cashTip} onChange={(e) => setCashTip(e.target.value)}
                  placeholder="0" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="cash-tip-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setCashDialogOrder(null)} variant="outline" className="flex-1 rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white hover:bg-[#F3EDE5]">Annuler</Button>
                <Button onClick={recordCashPayment} disabled={!cashAmount}
                  className="flex-1 bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl" data-testid="confirm-cash-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Confirmer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Dialog */}
      {discountDialogOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDiscountDialogOrder(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="discount-dialog">
            <h3 className="font-bold text-lg text-[#2D241D] mb-4 flex items-center gap-2">
              <Percent className="w-5 h-5 text-[#D4A853]" /> Appliquer une remise
            </h3>
            <p className="text-sm text-[#6E5E52] mb-4">Table {discountDialogOrder.table_id} - Total: {formatPrice(discountDialogOrder.total)}</p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button onClick={() => setDiscountType('percent')} variant={discountType === 'percent' ? 'default' : 'outline'}
                  className={`flex-1 rounded-xl ${discountType === 'percent' ? 'bg-[#D4A853] hover:bg-[#C49A48] text-white' : 'border-[#E5DCD3]'}`} data-testid="discount-type-percent">
                  <Percent className="w-4 h-4 mr-1" /> %
                </Button>
                <Button onClick={() => setDiscountType('fixed')} variant={discountType === 'fixed' ? 'default' : 'outline'}
                  className={`flex-1 rounded-xl ${discountType === 'fixed' ? 'bg-[#D4A853] hover:bg-[#C49A48] text-white' : 'border-[#E5DCD3]'}`} data-testid="discount-type-fixed">
                  <DollarSign className="w-4 h-4 mr-1" /> THB
                </Button>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Valeur ({discountType === 'percent' ? '%' : 'THB'})</label>
                <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === 'percent' ? 'ex: 10' : 'ex: 50'}
                  className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="discount-value-input" />
              </div>
              {discountValue && (
                <div className="bg-[#FFF3E0] rounded-xl p-3 text-center">
                  <p className="text-sm text-[#6E5E52]">Reduction:</p>
                  <p className="text-lg font-bold text-[#E16833]">
                    -{discountType === 'percent' ? formatPrice(Math.round(discountDialogOrder.total * parseFloat(discountValue) / 100)) : formatPrice(parseFloat(discountValue))}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => setDiscountDialogOrder(null)} variant="outline" className="flex-1 rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white hover:bg-[#F3EDE5]">Annuler</Button>
                <Button onClick={applyDiscount} disabled={!discountValue}
                  className="flex-1 bg-[#D4A853] hover:bg-[#C49A48] text-white rounded-xl" data-testid="confirm-discount-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Appliquer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
